import React, { createContext, useContext, useMemo, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import { APPS } from "../apps";

// open: taskbar order (order of opening). stack: z-order, back to front.
const initialState = {
  open: ["portfolio"],
  stack: ["portfolio"],
  minimized: [],
  focused: "portfolio",
};

const without = (list, id) => list.filter((x) => x !== id);

const topVisible = (stack, minimized) =>
  [...stack].reverse().find((id) => !minimized.includes(id)) ?? null;

const reducer = (state, { type, id }) => {
  // Shutting down closes every window, including the portfolio, as a real reboot would.
  if (type === "reset") {
    return { open: [], stack: [], minimized: [], focused: null };
  }
  if (!APPS[id]) {
    throw new Error(`Unknown app: ${id}`);
  }

  switch (type) {
    case "activate": {
      const isTop = state.stack[state.stack.length - 1] === id;
      if (state.focused === id && isTop && !state.minimized.includes(id)) {
        return state;
      }
      return {
        open: state.open.includes(id) ? state.open : [...state.open, id],
        stack: [...without(state.stack, id), id],
        minimized: without(state.minimized, id),
        focused: id,
      };
    }
    case "minimize": {
      const minimized = [...without(state.minimized, id), id];
      return { ...state, minimized, focused: topVisible(state.stack, minimized) };
    }
    case "close": {
      if (!APPS[id].buttons.includes("close")) {
        throw new Error(`App ${id} cannot be closed`);
      }
      const stack = without(state.stack, id);
      const minimized = without(state.minimized, id);
      return {
        open: without(state.open, id),
        stack,
        minimized,
        focused: topVisible(stack, minimized),
      };
    }
    default:
      throw new Error(`Unknown window action: ${type}`);
  }
};

const WindowManagerContext = createContext(null);

export const WindowManagerProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(
    () => ({
      state,
      // Opens the window if needed, then restores and raises it.
      activate: (id) => dispatch({ type: "activate", id }),
      minimize: (id) => dispatch({ type: "minimize", id }),
      close: (id) => dispatch({ type: "close", id }),
      reset: () => dispatch({ type: "reset" }),
    }),
    [state]
  );

  return <WindowManagerContext.Provider value={value}>{children}</WindowManagerContext.Provider>;
};

export const useWindowManager = () => {
  const ctx = useContext(WindowManagerContext);
  if (!ctx) {
    throw new Error("useWindowManager must be used inside WindowManagerProvider");
  }
  return ctx;
};

// Shortcuts into the portfolio (desktop icons, Documents) route it to a page and bring it forward.
export const useOpenPage = () => {
  const navigate = useNavigate();
  const { activate } = useWindowManager();
  return (path) => {
    navigate(path);
    activate("portfolio");
  };
};
